import Vue from 'vue'
import Vuex from 'vuex'
import { queryWebMenu, getMenuHtmlDetail } from '@/api/menu'

Vue.use(Vuex)

const STORAGE_KEY = 'vuex_menu'

// 构建菜单树（统一用字符串做 key，避免 id/parentId 数字与字符串不一致导致子菜单挂错或变成顶级）
function buildMenuTree (menuList) {
  const menuMap = {}
  const rootMenus = []
  const idKey = (id) => (id === undefined || id === null ? '' : String(id))

  if (!Array.isArray(menuList) || menuList.length === 0) return []

  menuList.forEach(menu => {
    const key = idKey(menu.id)
    menuMap[key] = { ...menu, children: [] }
  })

  menuList.forEach(menu => {
    const menuItem = menuMap[idKey(menu.id)]
    const pId = menu.parentId
    if (pId === '-1' || pId === -1 || pId === '' || pId === undefined || pId === null) {
      rootMenus.push(menuItem)
    } else {
      const parent = menuMap[idKey(pId)]
      if (parent) {
        parent.children.push(menuItem)
      } else {
        rootMenus.push(menuItem)
      }
    }
  })

  rootMenus.forEach(menu => {
    if (menu.children && menu.children.length > 0) {
      menu.children.sort((a, b) => (a.sort || 0) - (b.sort || 0))
    }
  })
  return rootMenus
}

function loadMenuFromSession () {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { menuList: [], allMenus: [] }
    const data = JSON.parse(raw)
    return {
      menuList: Array.isArray(data.menuList) ? data.menuList : [],
      allMenus: Array.isArray(data.allMenus) ? data.allMenus : []
    }
  } catch (e) {
    return { menuList: [], allMenus: [] }
  }
}

function saveMenuToSession (menuList, allMenus) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      menuList: menuList || [],
      allMenus: allMenus || []
    }))
  } catch (e) {
    console.warn('菜单写入 sessionStorage 失败:', e)
  }
}

const saved = loadMenuFromSession()
// 从 session 恢复时用扁平 allMenus 重新建树，避免旧缓存里树不完整（例如「关于果速达」只显示 1 个子项）
const initialMenuList = (saved.allMenus && saved.allMenus.length > 0)
  ? buildMenuTree(saved.allMenus).sort((a, b) => (a.sort || 0) - (b.sort || 0))
  : (saved.menuList || [])

export default new Vuex.Store({
  state: {
    menuList: initialMenuList,
    allMenus: saved.allMenus || [],
    // 全局配置（webMenuId=1）：logo、页脚、轮播等，与 layout 首页共用
    globalConfig: {
      logo: '',
      remarkInfo: '',
      authInfo: '',
      qrCodes: [],
      banners: []
    }
  },
  mutations: {
    SET_MENU_LIST (state, menuList) {
      state.menuList = menuList
    },
    SET_ALL_MENUS (state, allMenus) {
      state.allMenus = allMenus
    },
    SET_GLOBAL_CONFIG (state, config) {
      if (config && typeof config === 'object') {
        state.globalConfig = {
          logo: config.logo || '',
          remarkInfo: config.remarkInfo || '',
          authInfo: config.authInfo || '',
          qrCodes: Array.isArray(config.qrCodes) ? config.qrCodes : [],
          banners: Array.isArray(config.banners) ? config.banners : []
        }
      }
    }
  },
  actions: {
    // 获取全局配置（与 layout 页脚、首页轮播共用）
    async fetchGlobalConfig ({ commit }) {
      try {
        const res = await getMenuHtmlDetail({ webMenuId: '1' })
        if (res && res.result && res.result.htmlContent) {
          const htmlContent = res.result.htmlContent
          const config = typeof htmlContent === 'string' ? JSON.parse(htmlContent) : htmlContent
          commit('SET_GLOBAL_CONFIG', config)
        }
      } catch (e) {
        console.warn('获取全局配置失败:', e)
      }
    },
    // 获取菜单列表
    async fetchMenuList ({ commit }) {
      try {
        const res = await queryWebMenu({ type: 1 })
        if (res && res.result && res.result.records) {
          // 过滤菜单：排除 code 为 "W0001" 的，以及 type 不是 1 的
          const filteredRecords = res.result.records.filter(menu => {
            // 排除 code 为 "W0001" 的菜单
            if (menu.code === 'W0001') {
              return false
            }
            // 只显示 type 为数字 1 的菜单
            if (menu.type !== 1) {
              return false
            }
            return true
          })
          
          // 保存所有菜单（扁平结构）
          commit('SET_ALL_MENUS', filteredRecords)
          
          // 将菜单列表转换为树形结构
          const menuTree = buildMenuTree(filteredRecords)
          // 按 sort 排序
          menuTree.sort((a, b) => (a.sort || 0) - (b.sort || 0))
          
          // 保存树形菜单
          commit('SET_MENU_LIST', menuTree)
          saveMenuToSession(menuTree, filteredRecords)
        }
      } catch (error) {
        console.error('获取菜单失败:', error)
        commit('SET_MENU_LIST', [])
        commit('SET_ALL_MENUS', [])
        saveMenuToSession([], [])
      }
    }
  },
  getters: {
    // 全局配置（与 layout / home 共用）
    globalConfig: state => state.globalConfig,
    globalConfigLogo: state => state.globalConfig.logo,
    globalConfigRemarkInfo: state => state.globalConfig.remarkInfo,
    globalConfigAuthInfo: state => state.globalConfig.authInfo,
    globalConfigQrCodes: state => state.globalConfig.qrCodes,
    globalConfigBanners: state => state.globalConfig.banners,
    // 获取菜单列表（树形结构）
    menuList: state => state.menuList,
    // 获取所有菜单（扁平结构）
    allMenus: state => state.allMenus,
    // 根据 ID 获取菜单
    getMenuById: state => id => {
      return state.allMenus.find(menu => menu.id === id)
    },
    // 根据 code 获取菜单
    getMenuByCode: state => code => {
      return state.allMenus.find(menu => menu.code && menu.code.toUpperCase() === code.toUpperCase())
    },
    // 获取产品中心菜单
    getProductCenterMenu: state => {
      return state.allMenus.find(menu => {
        const name = (menu.name || '').toLowerCase()
        return name.includes('产品中心') && (!menu.parentId || menu.parentId === '-1')
      })
    },
    // 获取产品中心下的子菜单（系列）
    getProductSeries: (state, getters) => {
      const productCenterMenu = getters.getProductCenterMenu
      if (!productCenterMenu) {
        return []
      }
      return state.allMenus
        .filter(menu => menu.parentId === productCenterMenu.id && menu.type === 1)
        .sort((a, b) => (a.sort || 0) - (b.sort || 0))
    }
  }
})
